import {ManufacturingStaticData} from "./supporting/ManufacturingStaticData";
import {Criteria} from "./supporting/Criteria";
import {DataObject} from "./DataObject";
import {Address} from "./Address";
import {RateConfigDocument} from "./rate/RateConfig";

export class Facility implements DataObject  {
  ID: number;
  name: string;
  friendlyName: string;
  network_ID: number;
  admin_ID: number;
  /**
   * Whether this Facility / PrintNode is currently active. Backed by
   * `printnode.active TINYINT(1) NOT NULL DEFAULT 1` on the server
   * side (added 2026-05-13). Inactive facilities are suppressed
   * from rate-config editor pickers and the "Compare with facility
   * rates" peer selector; the underlying rate documents stay
   * readable for historical / audit purposes.
   * See `docs/contracts/shared/ActiveFlags.md`.
   */
  active: boolean = true;
  invoiceClientId: number;
  invoiceRecurringId: number;
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  email: string;
  phone: string;
  toFirstName: string;
  toLastName: string;
  toAddress1: string;
  toAddress2: string;
  toCity: string;
  toState: string;
  toPostalCode: string;
  toCountry: string;
  toEmail: string;
  toPhone: string;
  currency: string;
  secret: string;
  resourceBuckets: string;
  engines: string;
  impositions: string;
  printLineData: string;
  shippingRateTables: string;
  carriers: string;
  shipping: string;
  tradabilityTariffCodes: string;
  tradabilityAnswers: string;
  handlesInventory: string;
  languageStrings: string;
  productionRate: string;
  /** JSON-serialized {@link RateConfigDocument} for this facility. */
  rateConfig: string;
  /**
   * JSON-serialized packing/packaging {@link RateConfigDocument} for this
   * facility — what SnowfallPress pays the facility for picking, packing
   * and packaging operations. Parallel to {@link rateConfig}; same
   * document shape, evaluated against package-aware inputs (see
   * shared/PackingRateConfig.md).
   */
  packingRateConfig: string;
  lastUpdate: Date;
  invoiceCompany: string;
  manufacturingStaticData: ManufacturingStaticData;

  constructor ( obj ) {
    this.name = '';
    if ( obj && obj['ID']) {
      this.ID = obj['ID'];
    }
    this.update(obj);
    if (obj && obj.manufacturingStaticData ) {
      this.manufacturingStaticData = new ManufacturingStaticData(obj.manufacturingStaticData);
    }
  }
  public getId() {
    return this.ID;
  }

  public getOrderValue() {
    return this.ID;
  }

  update (obj: any) {
    if (obj) {
      for (const prop of Object.keys(obj)) {
        if (obj[prop] !== null && (prop !== 'ID') && ( typeof obj[prop] === 'string' || typeof obj[prop] === 'number' || typeof obj[prop] === 'boolean' || obj[prop] instanceof String || obj[prop] instanceof Boolean || obj[prop] instanceof Number || obj[prop] instanceof Date )) {
          this[prop] = obj[prop];
        }
      }
      // Coerce `active` to a real boolean. MySQL TINYINT(1) often comes
      // across the wire as the numbers 0 / 1 rather than false / true,
      // which silently breaks the canonical `entity.active !== false`
      // filter (0 !== false is true). The contract
      // docs/contracts/shared/ActiveFlags.md treats undefined/missing as
      // active so legacy rows continue to render.
      if (obj.active === undefined || obj.active === null) {
        this.active = true;
      } else {
        this.active = !!obj.active && obj.active !== 0;
      }
    }
  }


  public getManufacturingStaticData(): ManufacturingStaticData {
        if ( ! ( this.manufacturingStaticData instanceof ManufacturingStaticData ) ) {
            this.manufacturingStaticData = new ManufacturingStaticData( this.manufacturingStaticData );
        }
        return this.manufacturingStaticData;
    }

    // stringifyManufacturingStaticData() {
    //     this.getManufacturingStaticData();
    //     this.manufacturingStaticData = JSON.stringify(this.manufacturingStaticDataObject);
    // }
  getAddress() {
    return new Address(this);
  }

  /**
   * Parse {@link rateConfig} into a {@link RateConfigDocument}. Returns an
   * empty document on missing/invalid JSON so callers can edit it freely.
   *
   * Tolerates servers that hand back the column already-parsed (as a nested
   * object) rather than as a JSON string.
   */
  getRateConfig(): RateConfigDocument {
    const v: any = this.rateConfig;
    if (v && typeof v === 'object') { return v as RateConfigDocument; }
    if (typeof v === 'string' && v.length > 0) {
      try { return JSON.parse(v) as RateConfigDocument; }
      catch (e) { console.log(`Error parsing Facility.rateConfig for facility ${this.ID}`); }
    }
    return {};
  }

  /** Serialize a {@link RateConfigDocument} into the {@link rateConfig} column. */
  setRateConfig(doc: RateConfigDocument): void {
    this.rateConfig = JSON.stringify(doc || {});
  }

  /**
   * Parse {@link packingRateConfig} into a {@link RateConfigDocument}.
   * Returns an empty document on missing/invalid JSON. Parallel to
   * {@link getRateConfig} but for packing/packaging rates rather than
   * production.
   */
  getPackingRateConfig(): RateConfigDocument {
    const v: any = this.packingRateConfig;
    if (v && typeof v === 'object') { return v as RateConfigDocument; }
    if (typeof v === 'string' && v.length > 0) {
      try { return JSON.parse(v) as RateConfigDocument; }
      catch (e) { console.log(`Error parsing Facility.packingRateConfig for facility ${this.ID}`); }
    }
    return {};
  }

  /** Serialize a {@link RateConfigDocument} into the {@link packingRateConfig} column. */
  setPackingRateConfig(doc: RateConfigDocument): void {
    this.packingRateConfig = JSON.stringify(doc || {});
  }
}
